import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../Layout/DashboardLayout";
import { Button, Icon, companyID, employeeID } from "../../Common/UIComponents";
import { getEmployeeID, getCompanyID } from "../../Common/UIComponents";
// TODO: import { saveLabUtilization } from "../../../API_Service/Settings_Slice";

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const PROJECTS = [
  { value: "-1", label: "Internal Project" },
  { value: "1",  label: "AP0026_Infotainment System" },
  { value: "2",  label: "AP0031_Control Unit" },
  { value: "3",  label: "20251001_Amplifiers_220V" },
  { value: "4",  label: "20251002_Amplifiers_440v" },
  { value: "5",  label: "20251003_Infotainment Sys" },
];
const TEST_TYPES = ["Bio-Chemical Titration", "Stress Endurance Protocol", "Stability Calibration", "High-Throughput Screen"];
const TEST_NAMES = ["Iteration_v2_Beta", "Cryo-Unit-04-Run", "QSR-HTS-001", "NNB-SEP-Alpha", "HDD-Stability-R3"];
const ACTIVITIES = ["Phase 1 – Setup", "Phase 2 – Calibration", "Phase 3 – Sample Analysis", "Phase 4 – Endpoint Detection"];
const EMPLOYEES  = ["Admin", "John Smith", "Priya Rajan", "Arjun Kumar", "Sunita Rao"];

// Dummy existing bookings for the lab
const DUMMY_BOOKINGS = [
  { id: 1, project: "AP0026_Infotainment System", testName: "Iteration_v2_Beta",  startTime: "08:00 AM", endTime: "10:30 AM", officer: "Admin",       status: "Active"     },
  { id: 2, project: "20251001_Amplifiers_220V",   testName: "QSR-HTS-001",        startTime: "11:00 AM", endTime: "01:00 PM", officer: "Priya Rajan",  status: "Active"     },
  { id: 3, project: "AP0031_Control Unit",         testName: "NNB-SEP-Alpha",      startTime: "02:00 PM", endTime: "04:30 PM", officer: "Arjun Kumar",  status: "Completing" },
  { id: 4, project: "20251002_Amplifiers_440v",   testName: "HDD-Stability-R3",   startTime: "05:00 PM", endTime: "07:00 PM", officer: "Sunita Rao",   status: "Queued"     },
];

const BOOKING_STATUS = {
  Active:     { pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-400" },
  Completing: { pill: "bg-amber-100 text-amber-700",     dot: "bg-amber-400"   },
  Queued:     { pill: "bg-sky-100 text-sky-600",          dot: "bg-sky-400"     },
};

const EMPTY_FORM = {
  project: "", projectId: "", testType: "", testName: "", activity: "",
  startDate: "", startTime: "", endDate: "", endTime: "",
  temperature: "", humidity: "", remarks: "",
  to: "Admin", tqe: "", onBehalfOf: "",
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const inputCls  = "w-full h-11 bg-[#f1f4f4] border border-transparent rounded-xl px-4 text-sm font-medium text-[#181c1d] focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:bg-white transition-all placeholder:text-[#9eabac]";
const selectCls = "w-full h-11 bg-[#f1f4f4] border border-transparent rounded-xl px-4 text-sm font-medium text-[#181c1d] focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:bg-white transition-all appearance-none cursor-pointer";

function Field({ label, required, error, children, col = 1 }) {
  return (
    <div className={col === 2 ? "col-span-2" : ""}>
      <label className="block text-[10px] font-bold text-[#08757C] uppercase tracking-widest mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function SelectInput({ value, onChange, options, placeholder, error }) {
  const isObject = options.length > 0 && typeof options[0] === "object";
  return (
    <div className="relative">
      <select
        className={`${selectCls} ${error ? "ring-2 ring-red-400" : ""}`}
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">{placeholder ?? "Select"}</option>
        {isObject
          ? options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
          : options.map(o => <option key={o} value={o}>{o}</option>)
        }
      </select>
      <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#6e797a] text-[18px]">expand_more</span>
    </div>
  );
}

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────
export default function LabUtilizationCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const lab    = location.state?.lab ?? null;
  
  const isEdit = location.state?.mode === "edit";

  const [form,      setForm]      = useState(isEdit && location.state?.data ? location.state.data : EMPTY_FORM);
  const [errors,    setErrors]    = useState({});
  const [isSaving,  setIsSaving]  = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved,     setSaved]     = useState(false);
  const [activeTab, setActiveTab] = useState("new"); // "new" | "booked"

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: undefined }));
  };

  const isInternalProject = form.projectId === "-1";

  const validate = () => {
    const e = {};
    if (!form.projectId) e.project  = "Required";
    if (!form.testType)  e.testType = "Required";
    if (!isInternalProject && !form.testName) e.testName = "Required";
    if (!form.activity)  e.activity = "Required";
    if (!form.startDate || !form.startTime) e.startTime = "Required";
    if (!form.endDate   || !form.endTime)   e.endTime   = "Required";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setIsSaving(true);
    setSaveError(null);
    Promise.resolve()
      .then(() => {
        if (isEdit) {
          navigate("/lab-utilization", { state: { refresh: true, editSuccess: true } });
        } else {
          setSaved(true);
          setForm(EMPTY_FORM);
          setTimeout(() => setSaved(false), 3500);
        }
      })
      .catch(err => setSaveError(err?.message ?? "Failed to save"))
      .finally(() => setIsSaving(false));
  };

  // Today's date string for default
  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  return (
    <DashboardLayout>
      <div className="px-8 py-8 bg-[#f7fafa] min-h-screen">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
          <span>Lab Management</span>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <button onClick={() => navigate("/lab-utilization")} className="hover:text-[#08757C] transition-colors">
            Setup Utilization
          </button>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#08757C] font-semibold">{lab?.labName ?? "Lab"}</span>
        </nav>

        {/* Page header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-[#08757C] tracking-tight">
              {lab?.labName ?? "Lab"} — Setup
            </h1>
            <p className="text-slate-500 mt-1 text-sm font-medium">
              Configure a new test setup for this lab.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Shift Change Over */}
            <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold shadow-sm hover:opacity-90 transition-all"
              style={{ background: "#c0392b" }}>
              <span className="material-symbols-outlined text-[18px]">swap_horiz</span>
              Shift Change Over
            </button>
            {/* Switch Lab */}
            <button
              onClick={() => navigate("/lab-utilization")}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm hover:opacity-90 transition-all border border-[#08757C] text-[#08757C] bg-white"
            >
              <span className="material-symbols-outlined text-[18px]">sync_alt</span>
              Switch Lab
            </button>
          </div>
        </div>

        {/* Main layout: left form + right panel */}
        <div className="flex gap-6 items-start">

          {/* ── LEFT: Form Card ── */}
          <div className="flex-1 min-w-0 space-y-5">

            {/* Banners */}
            {saved && (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3.5">
                <span className="material-symbols-outlined text-emerald-500 text-[22px]" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                <div>
                  <p className="text-sm font-bold text-emerald-700">Setup created successfully!</p>
                  <p className="text-xs text-emerald-600 mt-0.5">You can create another or switch labs.</p>
                </div>
              </div>
            )}
            {saveError && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3.5">
                <span className="material-symbols-outlined text-red-400 text-[20px]">error</span>
                <p className="text-sm font-semibold text-red-600">{saveError}</p>
              </div>
            )}

            {/* Section 1 — Test Identity */}
            <div className="bg-white rounded-2xl border border-[#e5e9e9] shadow-sm px-6 pt-6 pb-7">
              <div className="flex items-center gap-3 pb-4 border-b border-[#f1f4f4] mb-5">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "#08757C15" }}>
                  <span className="material-symbols-outlined text-[18px]" style={{ color: "#08757C" }}>science</span>
                </div>
                <div>
                  <p className="text-sm font-extrabold text-[#181c1d]">Test Identity</p>
                  <p className="text-[11px] text-[#6e797a]">Select the project and define the test</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Project" required error={errors.project}>
                  <SelectInput
                    value={form.projectId}
                    onChange={v => {
                      const proj = PROJECTS.find(p => p.value === v);
                      setForm(f => ({ ...f, projectId: v, project: proj?.label ?? "", testName: "" }));
                      setErrors(e => ({ ...e, project: undefined, testName: undefined }));
                    }}
                    options={PROJECTS}
                    error={errors.project}
                  />
                </Field>
                <Field label="Test Type" required error={errors.testType}>
                  <SelectInput value={form.testType} onChange={v => set("testType", v)} options={TEST_TYPES} error={errors.testType} />
                </Field>
                {!isInternalProject && (
                  <Field label="Test Name" required error={errors.testName}>
                    <SelectInput value={form.testName} onChange={v => set("testName", v)} options={TEST_NAMES} error={errors.testName} />
                  </Field>
                )}
                <Field label="Activity" required error={errors.activity}>
                  <SelectInput value={form.activity} onChange={v => set("activity", v)} options={ACTIVITIES} error={errors.activity} />
                </Field>
              </div>
            </div>

            {/* Section 2 — Schedule */}
            <div className="bg-white rounded-2xl border border-[#e5e9e9] shadow-sm px-6 pt-6 pb-7">
              <div className="flex items-center gap-3 pb-4 border-b border-[#f1f4f4] mb-5">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "#08757C15" }}>
                  <span className="material-symbols-outlined text-[18px]" style={{ color: "#08757C" }}>calendar_month</span>
                </div>
                <p className="text-sm font-extrabold text-[#181c1d]">Schedule</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Start */}
                <Field label="Start Date & Time" required error={errors.startTime}>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      className={`${inputCls} ${errors.startTime ? "ring-2 ring-red-400" : ""}`}
                      value={form.startDate}
                      onChange={e => set("startDate", e.target.value)}
                    />
                    <input
                      type="time"
                      className={`${inputCls} ${errors.startTime ? "ring-2 ring-red-400" : ""}`}
                      value={form.startTime}
                      onChange={e => set("startTime", e.target.value)}
                    />
                  </div>
                </Field>
                {/* End */}
                <Field label="End Date & Time" required error={errors.endTime}>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      className={`${inputCls} ${errors.endTime ? "ring-2 ring-red-400" : ""}`}
                      value={form.endDate}
                      onChange={e => set("endDate", e.target.value)}
                    />
                    <input
                      type="time"
                      className={`${inputCls} ${errors.endTime ? "ring-2 ring-red-400" : ""}`}
                      value={form.endTime}
                      onChange={e => set("endTime", e.target.value)}
                    />
                  </div>
                </Field>
              </div>
            </div>

            {/* Section 3 — Environment */}
            <div className="bg-white rounded-2xl border border-[#e5e9e9] shadow-sm px-6 pt-6 pb-7">
              <div className="flex items-center gap-3 pb-4 border-b border-[#f1f4f4] mb-5">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "#08757C15" }}>
                  <span className="material-symbols-outlined text-[18px]" style={{ color: "#08757C" }}>thermostat</span>
                </div>
                <div>
                  <p className="text-sm font-extrabold text-[#181c1d]">Environment</p>
                  <p className="text-[11px] text-[#6e797a]">Optional — triggers alerts if changed mid-run</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Temperature (°C)">
                  <div className="relative">
                    <input className={inputCls} type="number" step="0.1" placeholder="28" value={form.temperature} onChange={e => set("temperature", e.target.value)} />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#6e797a]">°C</span>
                  </div>
                </Field>
                <Field label="Humidity (%)">
                  <div className="relative">
                    <input className={inputCls} type="number" placeholder="40" value={form.humidity} onChange={e => set("humidity", e.target.value)} />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#6e797a]">%</span>
                  </div>
                </Field>
              </div>
            </div>

            {/* Section 4 — Officers + Remarks */}
            <div className="bg-white rounded-2xl border border-[#e5e9e9] shadow-sm px-6 pt-6 pb-7">
              <div className="flex items-center gap-3 pb-4 border-b border-[#f1f4f4] mb-5">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "#08757C15" }}>
                  <span className="material-symbols-outlined text-[18px]" style={{ color: "#08757C" }}>groups</span>
                </div>
                <p className="text-sm font-extrabold text-[#181c1d]">Officers & Remarks</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="TO">
                  <input className={inputCls} value={form.to} readOnly />
                </Field>
                <Field label="TQE">
                  <SelectInput value={form.tqe} onChange={v => set("tqe", v)} options={EMPLOYEES} placeholder="Select Employee" />
                </Field>
                <Field label="On Behalf Of">
                  <SelectInput value={form.onBehalfOf} onChange={v => set("onBehalfOf", v)} options={EMPLOYEES} placeholder="Select Employee" />
                </Field>
                <Field label="Remarks">
                  <textarea
                    className={`${inputCls} h-11 py-2.5 resize-none`}
                    placeholder="Additional notes…"
                    value={form.remarks}
                    onChange={e => set("remarks", e.target.value)}
                  />
                </Field>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center gap-3 pb-6">
              <Button variant="primary" onClick={handleSubmit} disabled={isSaving}>
                <Icon name={isEdit ? "save" : "add_task"} className="mr-1 text-[18px]" />
                {isSaving ? "Saving…" : isEdit ? "Save Changes" : "Submit Setup"}
              </Button>
              <Button variant="ghost" onClick={() => { setForm(EMPTY_FORM); setErrors({}); }}>
                Reset
              </Button>
              <Button variant="ghost" onClick={() => navigate("/lab-utilization")}>
                Cancel
              </Button>
            </div>
          </div>

          {/* ── RIGHT: Lab context panel ── */}
          <div className="w-80 flex-shrink-0 space-y-4 sticky top-6">

            {/* Lab identity card */}
            <div className="rounded-2xl overflow-hidden border border-[#e5e9e9] shadow-sm">
              <div className="px-5 py-5 flex items-center gap-4" style={{ background: "linear-gradient(135deg, #1a2472 0%, #3040b0 100%)" }}>
                <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-white text-[24px]">biotech</span>
                </div>
                <div>
                  <h3 className="font-extrabold text-white text-base leading-tight">{lab?.labName ?? "Lab"}</h3>
                  <p className="text-white/60 text-[11px] mt-0.5">
                    {lab?.slotsUsed ?? 0}/{lab?.totalSlots ?? 8} slots in use
                  </p>
                </div>
              </div>
              {/* Slot bar */}
              {lab && (
                <div className="bg-white px-5 py-3">
                  <div className="h-1.5 bg-[#f1f4f4] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.round((lab.slotsUsed / lab.totalSlots) * 100)}%`,
                        background: lab.slotsUsed / lab.totalSlots >= 1 ? "#ef4444" : lab.slotsUsed / lab.totalSlots >= 0.7 ? "#f59e0b" : "#10b981",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Tabs: Booked slots */}
            <div className="bg-white rounded-2xl border border-[#e5e9e9] shadow-sm overflow-hidden">
              <div className="flex border-b border-[#f1f4f4]">
                {[
                  { key: "booked", label: "Booked Slots", icon: "event_busy" },
                  { key: "info",   label: "Guidelines",   icon: "info"       },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-bold transition-colors ${
                      activeTab === tab.key
                        ? "text-[#08757C] border-b-2 border-[#08757C]"
                        : "text-[#6e797a] hover:text-[#3e494a]"
                    }`}
                  >
                    <span className="material-symbols-outlined text-[15px]">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="px-4 py-4">
                {activeTab === "booked" ? (
                  DUMMY_BOOKINGS.length === 0 ? (
                    <div className="flex flex-col items-center py-6 gap-2 text-[#bdc9c9]">
                      <span className="material-symbols-outlined text-[32px]">event_available</span>
                      <p className="text-xs font-medium">No bookings today</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {DUMMY_BOOKINGS.map(b => {
                        const bs = BOOKING_STATUS[b.status] || BOOKING_STATUS.Active;
                        return (
                          <div key={b.id} className="rounded-xl border border-[#f1f4f4] p-3 space-y-2 hover:border-[#e5e9e9] transition-colors">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-[11px] font-bold text-[#181c1d] truncate">{b.testName}</p>
                                <p className="text-[10px] text-[#6e797a] truncate">{b.project}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold flex-shrink-0 ${bs.pill}`}>
                                {b.status}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[13px] text-[#6e797a]">schedule</span>
                                <span className="text-[10px] font-semibold text-[#3e494a]">{b.startTime} – {b.endTime}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="material-symbols-outlined text-[13px] text-[#6e797a]">person</span>
                                <span className="text-[10px] text-[#6e797a]">{b.officer}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : (
                  <div className="space-y-3 text-[11px] text-[#3e494a] leading-relaxed">
                    <div className="flex items-start gap-2">
                      <span className="material-symbols-outlined text-[16px] text-amber-500 flex-shrink-0 mt-0.5">warning</span>
                      <p>Calibrate all environmental sensors before starting a new test run.</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="material-symbols-outlined text-[16px] text-[#08757C] flex-shrink-0 mt-0.5">thermostat</span>
                      <p>Temperature and humidity changes trigger automated baseline alerts.</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="material-symbols-outlined text-[16px] text-blue-400 flex-shrink-0 mt-0.5">groups</span>
                      <p>Assign TQE and On Behalf Of officers for accountability tracking.</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="material-symbols-outlined text-[16px] text-slate-400 flex-shrink-0 mt-0.5">swap_horiz</span>
                      <p>Use <strong>Shift Change Over</strong> when handing off to another shift team.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
